// UserManagementPage.tsx - Admin page for managing users and page access
// Uses app_users, app_roles, app_permissions, app_user_permissions tables
import React, { useState, useEffect, useCallback } from 'react';
import { WIDGET_REGISTRY, getPageWidgets, type RoleCode } from './config/widgetRegistry';
import { 
  ALL_PAGES, 
  ROLE_DEFINITIONS, 
  ADMIN_ONLY_PAGES,
  getDefaultPermissions, 
  getRoleIcon, 
  getRoleColor 
} from './AuthContext';

// ============================================================================
// Types
// ============================================================================

interface User {
  user_key: number;
  user_guid: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role_key: number;
  role_code: string;
  role_name: string;
  supplier_key: number | null;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  insurer_access: number[];
  workshop_access: number[];
  supplier_access: number[];
}

interface Role {
  role_key: number;
  role_code: string;
  role_name: string;
  role_description: string;
}

interface Insurer {
  insurer_key: number;
  insurer_name: string;
}

interface Supplier {
  supplier_key: number;
  supplier_name: string;
}

interface Workshop {
  workshop_key: number;
  workshop_name: string;
}

interface PagePermission {
  view: boolean;
  edit: boolean;
  export: boolean;
}

interface UserFormData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  role_key: number | null;
  insurer_keys: number[];
  supplier_keys: number[];
  workshop_keys: number[];
  is_active: boolean;
  page_permissions: Record<string, PagePermission>;
}

interface UserManagementPageProps {
  currentUserRole: string;
}

// ============================================================================
// Icons
// ============================================================================

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);

const XIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ShieldIcon = () => (
  <svg className="w-6 h-6 text-[#3a7de4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const LockIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
  </svg>
);

// ============================================================================
// Page Access Panel Component
// ============================================================================

const PageAccessPanel: React.FC<{
  permissions: Record<string, PagePermission>;
  onChange: (permissions: Record<string, PagePermission>) => void;
  roleCode: string | null;
  isCurrentUserAdmin: boolean;
  onResetToDefaults: () => void;
}> = ({ permissions, onChange, roleCode, isCurrentUserAdmin, onResetToDefaults }) => {
  
  // Group pages by category
  const pagesByCategory = ALL_PAGES.reduce((acc, page) => {
    if (!acc[page.category]) acc[page.category] = [];
    acc[page.category].push(page);
    return acc;
  }, {} as Record<string, typeof ALL_PAGES[number][]>);

  const categoryConfig: Record<string, { label: string; icon: string; order: number }> = {
    analytics: { label: 'Analytics', icon: '📊', order: 1 },
    data: { label: 'Data Management', icon: '📋', order: 2 },
    reporting: { label: 'Reporting', icon: '📄', order: 3 },
    executive: { label: 'Executive', icon: '💼', order: 4 },
    profiles: { label: 'Profiles', icon: '👤', order: 5 },
    admin: { label: 'Administration', icon: '⚙️', order: 6 },
  };

  const sortedCategories = Object.entries(pagesByCategory).sort(
    ([a], [b]) => (categoryConfig[a]?.order || 99) - (categoryConfig[b]?.order || 99)
  );

  const togglePage = (pageCode: string, field: 'view' | 'edit' | 'export') => {
    const current = permissions[pageCode] || { view: false, edit: false, export: false };
    const newPerms = { ...permissions };
    
    if (field === 'view' && current.view) {
      // Turning off view turns off everything
      newPerms[pageCode] = { view: false, edit: false, export: false };
    } else if (field === 'view' && !current.view) {
      newPerms[pageCode] = { ...current, view: true };
    } else {
      // For edit/export, ensure view is on
      newPerms[pageCode] = { ...current, [field]: !current[field], view: true };
    }
    
    onChange(newPerms);
  };

  const setAllInCategory = (category: string, enabled: boolean) => {
    const newPerms = { ...permissions };
    pagesByCategory[category]?.forEach(page => {
      // Skip admin pages for non-admin roles
      if (ADMIN_ONLY_PAGES.includes(page.code) && roleCode !== 'app_admin') {
        return;
      }
      newPerms[page.code] = { view: enabled, edit: false, export: enabled };
    });
    onChange(newPerms);
  };

  // Count enabled pages
  const enabledCount = Object.values(permissions).filter(p => p.view).length;
  const totalCount = ALL_PAGES.filter(p => !ADMIN_ONLY_PAGES.includes(p.code) || roleCode === 'app_admin').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-gray-700">Page Access Control</label>
          <p className="text-xs text-gray-500 mt-0.5">{enabledCount} of {totalCount} pages enabled</p>
        </div>
        <button
          type="button"
          onClick={onResetToDefaults}
          className="text-xs px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Reset to Role Defaults
        </button>
      </div>
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {sortedCategories.map(([category, pages]) => {
          const catConfig = categoryConfig[category] || { label: category, icon: '📁', order: 99 };
          const isAdminCategory = category === 'admin';
          const categoryEnabled = pages.filter(p => permissions[p.code]?.view).length;
          
          return (
            <div key={category} className="border-b last:border-b-0">
              {/* Category Header */}
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-base">{catConfig.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{catConfig.label}</span>
                  <span className="text-xs text-gray-400">({categoryEnabled}/{pages.length})</span>
                  {isAdminCategory && (
                    <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">Admin Only</span>
                  )}
                </div>
                {(!isAdminCategory || roleCode === 'app_admin') && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setAllInCategory(category, true)}
                      className="text-xs text-green-600 hover:text-green-800 font-medium"
                    >
                      Enable All
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllInCategory(category, false)}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Disable All
                    </button>
                  </div>
                )}
              </div>
              
              {/* Pages in Category */}
              <div className="divide-y divide-gray-100">
                {pages.map(page => {
                  const perm = permissions[page.code] || { view: false, edit: false, export: false };
                  const isAdminPage = ADMIN_ONLY_PAGES.includes(page.code);
                  const isDisabled = isAdminPage && roleCode !== 'app_admin';
                  
                  return (
                    <div 
                      key={page.code} 
                      className={`flex items-center justify-between px-4 py-2.5 ${
                        isDisabled ? 'opacity-40 bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${perm.view ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                            {page.name}
                          </span>
                          {isDisabled && (
                            <LockIcon />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate">{page.description}</p>
                      </div>
                      
                      <div className="flex items-center gap-6 ml-4">
                        <label className={`flex items-center gap-1.5 ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={perm.view}
                            onChange={() => togglePage(page.code, 'view')}
                            disabled={isDisabled}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-600">View</span>
                        </label>
                        
                        <label className={`flex items-center gap-1.5 ${isDisabled || !perm.view ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={perm.edit}
                            onChange={() => togglePage(page.code, 'edit')}
                            disabled={isDisabled || !perm.view}
                            className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-xs text-gray-600">Edit</span>
                        </label>
                        
                        <label className={`flex items-center gap-1.5 ${isDisabled || !perm.view ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={perm.export}
                            onChange={() => togglePage(page.code, 'export')}
                            disabled={isDisabled || !perm.view}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-xs text-gray-600">Export</span>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-xs text-amber-700">
          <strong>Note:</strong> Admin pages (EDA, ETL, Users) can only be accessed by System Administrators. 
          The Shareholders page is restricted to Shareholder and Admin roles by default.
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// User Modal Component
// ============================================================================

const UserModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  roles: Role[];
  insurers: Insurer[];
  suppliers: Supplier[];
  workshops: Workshop[];
  onSave: (data: UserFormData, userId?: number) => Promise<void>;
  currentUserRole: string;
}> = ({ isOpen, onClose, user, roles, insurers, suppliers, workshops, onSave, currentUserRole }) => {
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role_key: null,
    insurer_keys: [],
    supplier_keys: [],
    workshop_keys: [],
    is_active: true,
    page_permissions: {},
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'data' | 'pages' | 'widgets'>('details');
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  // widget_key → is_visible override map
  const [widgetPermissions, setWidgetPermissions] = useState<Record<string, boolean>>({});
  const [widgetPermissionsLoading, setWidgetPermissionsLoading] = useState(false);
  const [widgetPermissionsSaving, setWidgetPermissionsSaving] = useState(false);

  // Load user data when modal opens
  useEffect(() => {
    if (user) {
      const selectedRole = roles.find(r => r.role_key === user.role_key);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role_key: user.role_key,
        insurer_keys: user.insurer_access || [],
        supplier_keys: user.supplier_access || [],
        workshop_keys: user.workshop_access || [],
        is_active: user.is_active,
        page_permissions: {},
      });
      
      // Load user-specific permissions
      if (selectedRole) {
        loadUserPermissions(user.user_key, selectedRole.role_code);
      }
    } else {
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        role_key: null,
        insurer_keys: [],
        supplier_keys: [],
        workshop_keys: [],
        is_active: true,
        page_permissions: {},
      });
    }
    setError(null);
    setActiveTab('details');
  }, [user, isOpen, roles]);

  const loadUserPermissions = async (userKey: number, roleCode: string) => {
    setLoadingPermissions(true);
    try {
      const token = localStorage.getItem('xnuup_token');
      const response = await fetch(`/api/users/pages?user_key=${userKey}&role_code=${roleCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        console.warn(`loadUserPermissions: ${response.status} from ${response.url}`);
        setFormData(prev => ({ ...prev, page_permissions: getDefaultPermissions(roleCode) }));
        return;
      }
      const data = await response.json();
      if (data.success) {
        // Use user permissions if they exist, otherwise use role defaults
        const perms = data.data.hasUserOverrides
          ? data.data.userPermissions
          : data.data.rolePermissions;
        setFormData(prev => ({ ...prev, page_permissions: perms || getDefaultPermissions(roleCode) }));
      } else {
        setFormData(prev => ({ ...prev, page_permissions: getDefaultPermissions(roleCode) }));
      }
    } catch (err) {
      console.error('Failed to load permissions:', err);
      setFormData(prev => ({ ...prev, page_permissions: getDefaultPermissions(roleCode) }));
    } finally {
      setLoadingPermissions(false);
    }
  };

  const loadWidgetPermissions = async (userKey: number) => {
    setWidgetPermissionsLoading(true);
    try {
      const token = localStorage.getItem('xnuup_token');
      const resp = await fetch(`/api/users/widget-permissions?user_key=${userKey}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.success) {
        const map: Record<string, boolean> = {};
        (data.data.permissions || []).forEach((p: { widget_key: string; is_visible: boolean }) => {
          map[p.widget_key] = p.is_visible;
        });
        setWidgetPermissions(map);
      }
    } catch (e) {
      // silently ignore — table may not exist yet
    } finally {
      setWidgetPermissionsLoading(false);
    }
  };

  const saveWidgetPermissions = async (userKey: number) => {
    setWidgetPermissionsSaving(true);
    try {
      const token = localStorage.getItem('xnuup_token');
      const payload: Record<string, { page_code: string; is_visible: boolean }> = {};
      WIDGET_REGISTRY.forEach(w => {
        if (w.widget_key in widgetPermissions) {
          payload[w.widget_key] = { page_code: w.page_code, is_visible: widgetPermissions[w.widget_key] };
        }
      });
      await fetch('/api/users/widget-permissions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_key: userKey, widget_permissions: payload }),
      });
      // Notify any open dashboard session for this user to re-fetch their permissions
      window.dispatchEvent(new CustomEvent('widget-permissions-changed', { detail: { user_key: userKey } }));
    } catch (e) {
      // silently ignore
    } finally {
      setWidgetPermissionsSaving(false);
    }
  };

  const selectedRole = roles.find(r => r.role_key === formData.role_key);
  const roleCode = selectedRole?.role_code || '';
  
  const showInsurerSelect = ['insurer_admin', 'insurer_analyst'].includes(roleCode);
  const showSupplierSelect = roleCode === 'supplier_user';
  const showWorkshopSelect = roleCode === 'workshop_user';

  // Update permissions when role changes
  useEffect(() => {
    if (selectedRole && !user) {
      setFormData(prev => ({
        ...prev,
        page_permissions: getDefaultPermissions(selectedRole.role_code),
      }));
    }
  }, [selectedRole?.role_key, user]);

  // Load widget permissions when switching to widgets tab
  useEffect(() => {
    if (activeTab === 'widgets' && user?.user_key) {
      loadWidgetPermissions(user.user_key);
    }
  }, [activeTab, user?.user_key]);

  // Filter roles based on current user
  const availableRoles = roles.filter(r => {
    if (currentUserRole === 'app_admin') return true;
    return !['app_admin', 'shareholder'].includes(r.role_code);
  });

  const handleResetToDefaults = () => {
    if (roleCode) {
      setFormData(prev => ({ ...prev, page_permissions: getDefaultPermissions(roleCode) }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      if (!formData.email || !formData.role_key) {
        throw new Error('Email and role are required');
      }
      if (!user && !formData.password) {
        throw new Error('Password is required for new users');
      }
      await onSave(formData, user?.user_key);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {user ? `Edit User: ${user.first_name || user.email}` : 'Create New User'}
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <XIcon />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {[
            { id: 'details', label: 'User Details', icon: <UserIcon /> },
            { id: 'data', label: 'Data Access', icon: <LockIcon /> },
            { id: 'pages', label: 'Page Permissions', icon: <DocumentIcon /> },
            { id: 'widgets', label: 'Widget Access', icon: <DocumentIcon /> },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab.id 
                  ? 'text-blue-600 border-blue-600 bg-white' 
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6">
            {error && (
              <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Tab: User Details */}
            {activeTab === 'details' && (
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="john.doe@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password {user ? <span className="text-gray-400 font-normal">(leave blank to keep current)</span> : <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                    required={!user}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Role <span className="text-red-500">*</span></label>
                  <select
                    value={formData.role_key || ''}
                    onChange={(e) => {
                      const newRoleKey = e.target.value ? parseInt(e.target.value) : null;
                      const newRole = roles.find(r => r.role_key === newRoleKey);
                      setFormData({ 
                        ...formData, 
                        role_key: newRoleKey,
                        insurer_keys: [],
                        supplier_keys: [],
                        workshop_keys: [],
                        page_permissions: newRole ? getDefaultPermissions(newRole.role_code) : {},
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a role...</option>
                    {availableRoles.map(role => (
                      <option key={role.role_key} value={role.role_key}>
                        {getRoleIcon(role.role_code)} {role.role_name}
                      </option>
                    ))}
                  </select>
                  {selectedRole && ROLE_DEFINITIONS[roleCode] && (
                    <p className="mt-1.5 text-xs text-gray-500">
                      {ROLE_DEFINITIONS[roleCode].description}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Account is Active</label>
                </div>
              </div>
            )}

            {/* Tab: Data Access */}
            {activeTab === 'data' && (
              <div className="space-y-5">
                {!selectedRole && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    Please select a role first in the User Details tab.
                  </div>
                )}

                {showInsurerSelect && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Insurer Access</label>
                    <p className="text-xs text-gray-500 mb-2">Select which insurers this user can access data for.</p>
                    <div className="border border-gray-300 rounded-lg p-3 max-h-56 overflow-y-auto space-y-2">
                      {insurers.length === 0 ? (
                        <p className="text-sm text-gray-500 italic">No insurers available</p>
                      ) : (
                        insurers.map(insurer => (
                          <label key={insurer.insurer_key} className="flex items-center gap-2 cursor-pointer py-1">
                            <input
                              type="checkbox"
                              checked={formData.insurer_keys.includes(insurer.insurer_key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, insurer_keys: [...formData.insurer_keys, insurer.insurer_key] });
                                } else {
                                  setFormData({ ...formData, insurer_keys: formData.insurer_keys.filter(k => k !== insurer.insurer_key) });
                                }
                              }}
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">{insurer.insurer_name}</span>
                          </label>
                        ))
                      )}
                    </div>
                    {formData.insurer_keys.length > 0 && (
                      <p className="mt-1.5 text-xs text-blue-600">{formData.insurer_keys.length} insurer(s) selected</p>
                    )}
                  </div>
                )}

                {showSupplierSelect && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Supplier</label>
                    <p className="text-xs text-gray-500 mb-2">This user will only see data for their assigned supplier.</p>
                    <select
                      value={formData.supplier_keys[0] || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        supplier_keys: e.target.value ? [parseInt(e.target.value)] : [] 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a supplier...</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.supplier_key} value={supplier.supplier_key}>
                          {supplier.supplier_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {showWorkshopSelect && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Workshop</label>
                    <p className="text-xs text-gray-500 mb-2">This user will only see data for their assigned workshop.</p>
                    <select
                      value={formData.workshop_keys[0] || ''}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        workshop_keys: e.target.value ? [parseInt(e.target.value)] : [] 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a workshop...</option>
                      {workshops.map(workshop => (
                        <option key={workshop.workshop_key} value={workshop.workshop_key}>
                          {workshop.workshop_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedRole && ['app_admin', 'shareholder'].includes(roleCode) && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>{selectedRole.role_name}</strong> users have access to all data without restrictions.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tab: Page Permissions */}
            {activeTab === 'pages' && (
              loadingPermissions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <PageAccessPanel
                  permissions={formData.page_permissions}
                  onChange={(perms) => setFormData({ ...formData, page_permissions: perms })}
                  roleCode={roleCode}
                  isCurrentUserAdmin={currentUserRole === 'app_admin'}
                  onResetToDefaults={handleResetToDefaults}
                />
              )
            )}

            {/* Tab: Widget Access */}
            {activeTab === 'widgets' && (
              !user ? (
                <div className="text-center text-gray-500 py-12 text-sm">
                  Save the user first before configuring widget access.
                </div>
              ) : widgetPermissionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Header actions */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Override which widgets are visible for this user. Empty = use role defaults from registry.
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const all: Record<string, boolean> = {};
                          WIDGET_REGISTRY
                            .filter(w => formData.page_permissions[w.page_code]?.view)
                            .forEach(w => { all[w.widget_key] = true; });
                          setWidgetPermissions(all);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100"
                      >
                        Grant All
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const none: Record<string, boolean> = {};
                          WIDGET_REGISTRY
                            .filter(w => formData.page_permissions[w.page_code]?.view)
                            .forEach(w => { none[w.widget_key] = false; });
                          setWidgetPermissions(none);
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                      >
                        Revoke All
                      </button>
                      <button
                        type="button"
                        onClick={() => { setWidgetPermissions({}); }}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded hover:bg-gray-100"
                      >
                        Reset to Defaults
                      </button>
                      <button
                        type="button"
                        disabled={widgetPermissionsSaving}
                        onClick={() => user && saveWidgetPermissions(user.user_key)}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {widgetPermissionsSaving ? 'Saving…' : 'Save Widget Settings'}
                      </button>
                    </div>
                  </div>

                  {/* Widgets grouped by page */}
                  {Array.from(new Set(WIDGET_REGISTRY.map(w => w.page_code)))
                    .filter(page => formData.page_permissions[page]?.view)
                    .map(page => {
                    const pageWidgets = getPageWidgets(page);
                    const pageGranted = pageWidgets.filter(w => widgetPermissions[w.widget_key] === true).length;
                    const pageRevoked = pageWidgets.filter(w => widgetPermissions[w.widget_key] === false).length;
                    return (
                      <div key={page} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                          <span className="text-sm font-semibold text-gray-700 capitalize">{page}</span>
                          <div className="flex items-center gap-3">
                            {pageGranted > 0 && <span className="text-xs text-green-600">{pageGranted} granted</span>}
                            {pageRevoked > 0 && <span className="text-xs text-red-600">{pageRevoked} revoked</span>}
                            {pageGranted === 0 && pageRevoked === 0 && <span className="text-xs text-gray-400">defaults</span>}
                            <button
                              type="button"
                              onClick={() => {
                                const next = { ...widgetPermissions };
                                pageWidgets.forEach(w => { next[w.widget_key] = true; });
                                setWidgetPermissions(next);
                              }}
                              className="text-xs text-green-600 hover:underline"
                            >
                              All On
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const next = { ...widgetPermissions };
                                pageWidgets.forEach(w => { next[w.widget_key] = false; });
                                setWidgetPermissions(next);
                              }}
                              className="text-xs text-red-600 hover:underline"
                            >
                              All Off
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x">
                          {pageWidgets.map(w => {
                            const override = widgetPermissions[w.widget_key];
                            const isOn = override === true ? true : override === false ? false : w.default_visible;
                            const hasOverride = override !== undefined;
                            return (
                              <label
                                key={w.widget_key}
                                className={`flex items-start gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 ${hasOverride ? 'bg-blue-50' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  onChange={(e) => {
                                    setWidgetPermissions(prev => ({ ...prev, [w.widget_key]: e.target.checked }));
                                  }}
                                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                                    {w.label}
                                    <span className={`text-xs px-1 rounded ${w.category === 'kpi' ? 'bg-blue-100 text-blue-600' : w.category === 'chart' ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                                      {w.category}
                                    </span>
                                    {hasOverride && (
                                      <span className="text-xs text-blue-500">(override)</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-400 truncate">{w.description}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const UserManagementPage: React.FC<UserManagementPageProps> = ({ currentUserRole }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [insurers, setInsurers] = useState<Insurer[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem('xnuup_token');
    const headers = { Authorization: `Bearer ${token}` };

    const safeFetch = async (url: string) => {
      try {
        const res = await fetch(url, { headers });
        if (!res.ok) { console.warn(`fetchData: ${res.status} from ${url}`); return null; }
        return res.json();
      } catch (err) {
        console.error(`fetchData: failed to fetch ${url}`, err);
        return null;
      }
    };

    const [usersData, rolesData, insurersData, suppliersData, workshopsData] = await Promise.all([
      safeFetch('/api/users'),
      safeFetch('/api/users/roles'),
      safeFetch('/api/insurers'),
      safeFetch('/api/suppliers/list'),
      safeFetch('/api/workshops/list'),
    ]);

    if (usersData?.success) setUsers(usersData.data || []);
    if (rolesData?.success) setRoles(rolesData.data || []);
    if (insurersData?.success) setInsurers(insurersData.data || []);
    if (suppliersData?.success) setSuppliers(suppliersData.data || []);
    if (workshopsData?.success) setWorkshops(workshopsData.data || []);

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveUser = async (data: UserFormData, userId?: number) => {
    const token = localStorage.getItem('xnuup_token');
    const url = userId ? `/api/users?user_key=${userId}` : '/api/users';
    const method = userId ? 'PUT' : 'POST';

    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      let errMsg = `Server error ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody.error) errMsg = errBody.error;
      } catch { /* non-JSON body */ }
      throw new Error(errMsg);
    }

    const result = await response.json();
    if (!result.success) throw new Error(result.error || 'Save failed');

    // Save page permissions
    const userKey = userId || result.data?.user_key;
    if (userKey && Object.keys(data.page_permissions).length > 0) {
      const permRes = await fetch('/api/users/pages', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_key: userKey,
          permissions: data.page_permissions,
        }),
      });
      if (!permRes.ok) {
        console.warn(`handleSaveUser: permissions save failed with ${permRes.status}`);
      }
    }
    
    await fetchData();
  };

  const handleDeleteUser = async (userId: number) => {
    const token = localStorage.getItem('xnuup_token');
    const response = await fetch(`/api/users?user_key=${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      let errMsg = `Server error ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody.error) errMsg = errBody.error;
      } catch { /* non-JSON body */ }
      throw new Error(errMsg);
    }

    const result = await response.json();
    if (!result.success) throw new Error(result.error || 'Delete failed');
    
    setDeleteConfirm(null);
    setDeleteError(null);
    await fetchData();
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role_code === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Role stats
  const roleStats = roles.map(role => ({
    ...role,
    count: users.filter(u => u.role_code === role.role_code).length
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-100 rounded-xl">
            <ShieldIcon />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">User Management</h1>
            <p className="text-sm text-gray-500">{users.length} users • {roles.length} roles</p>
          </div>
        </div>
        <button
          onClick={() => { setEditingUser(null); setModalOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <PlusIcon />
          <span>Add User</span>
        </button>
      </div>

      {/* Role Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {roleStats.map(role => (
          <button 
            key={role.role_code} 
            onClick={() => setRoleFilter(roleFilter === role.role_code ? 'all' : role.role_code)}
            className={`p-3 rounded-xl border text-left transition-all ${
              roleFilter === role.role_code 
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <div className="flex items-center gap-2.5">
              <span className="text-xl">{getRoleIcon(role.role_code)}</span>
              <div>
                <div className="text-lg font-semibold text-gray-900">{role.count}</div>
                <div className="text-xs text-gray-500 truncate">{role.role_name}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
          >
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role.role_code} value={role.role_code}>
                {role.role_name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Data Access</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredUsers.map(user => (
                <tr key={user.user_key} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{user.first_name} {user.last_name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-${getRoleColor(user.role_code)}-100 text-${getRoleColor(user.role_code)}-700`}>
                      <span>{getRoleIcon(user.role_code)}</span>
                      {user.role_name}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">
                    {user.role_code === 'app_admin' && <span className="text-purple-600 font-medium">All Data</span>}
                    {user.role_code === 'shareholder' && <span className="text-indigo-600 font-medium">All Data (Read)</span>}
                    {user.insurer_access && user.insurer_access.length > 0 && (
                      <span className="text-blue-600">{user.insurer_access.length} Insurer(s)</span>
                    )}
                    {user.supplier_access && user.supplier_access.length > 0 && (
                      <span className="text-green-600">Supplier</span>
                    )}
                    {user.workshop_access && user.workshop_access.length > 0 && (
                      <span className="text-orange-600">Workshop</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">
                    {user.last_login_at 
                      ? new Date(user.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : <span className="text-gray-400 italic">Never</span>
                    }
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => { setEditingUser(user); setModalOpen(true); }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit user"
                      >
                        <PencilIcon />
                      </button>
                      {user.role_code !== 'app_admin' && (
                        <button
                          onClick={() => setDeleteConfirm(user.user_key)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete user"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-500">
                    <p className="text-base">No users found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Modal */}
      <UserModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setEditingUser(null); }}
        user={editingUser}
        roles={roles}
        insurers={insurers}
        suppliers={suppliers}
        workshops={workshops}
        onSave={handleSaveUser}
        currentUserRole={currentUserRole}
      />

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete User</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this user? This action cannot be undone.</p>
            {deleteError && (
              <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {deleteError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    setDeleteError(null);
                    await handleDeleteUser(deleteConfirm);
                  } catch (err: any) {
                    setDeleteError(err.message || 'Delete failed');
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;